/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable */
import React, { FC, useEffect, useMemo, useState } from 'react';
import PrimaryButton from '@/components/common/PrimaryButton';
import DefaultButton from '@/components/common/DefaultButton';
import UploadIcon from '@mui/icons-material/Upload';
import styles from './Projects.module.scss';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
} from '@mui/material';
import { useGetStreamKeys } from '@/adapters/stream-keys';
import { useSnackbar } from 'notistack';
import { useGetProjectList } from '@/adapters/projects';
import ApiV2 from '@/adapters/api-v2';
import Api from '@/adapters/api';
import Project from '@/types/project';
import ProjectListItem from '@/types/project-list-item';
import { buildChunkPlan, ChunkPlan, ChunkDescriptor, CHUNK_SIZE } from '@/utils/mp4-faststart';

const MAX_CONCURRENT_UPLOADS = 4;
const MAX_RETRIES = 3;

const RECENT_PROJECT_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

const parseProjectTimestamp = (project: ProjectListItem): number => {
  const createdValue = project.created ?? '';
  const parsed = Date.parse(createdValue);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }
  const numericId = Number(project.id);
  if (Number.isFinite(numericId)) {
    return numericId;
  }
  return Date.now();
};

const findMatchingProject = (
  projects: ProjectListItem[],
  streamKey: any,
): ProjectListItem | undefined => {
  if (!projects?.length) {
    return undefined;
  }

  const sortedProjects = [...projects].sort(
    (a, b) => parseProjectTimestamp(b) - parseProjectTimestamp(a),
  );
  const streamedProjects = sortedProjects.filter((project) => project.projectType === 'STREAMED');
  if (!streamedProjects.length) {
    return undefined;
  }

  if (streamKey?.eventId) {
    const eventMatch = streamedProjects.find((project) => project.eventId === streamKey.eventId);
    if (eventMatch) {
      return eventMatch;
    }
  }

  const recentMatch = streamedProjects.find(
    (project) => Date.now() - parseProjectTimestamp(project) < RECENT_PROJECT_WINDOW_MS,
  );
  if (recentMatch) {
    return recentMatch;
  }

  return streamedProjects[0];
};

/**
 * Build a chunk plan for any video file.
 * For MP4s, relocates moov atom for progressive streaming.
 * For other formats, falls back to sequential 10 MB slices.
 *
 * @reason Non-MP4 files don't have moov atoms but still benefit from chunked upload.
 */
async function getChunkPlan(file: File): Promise<ChunkPlan> {
  try {
    return await buildChunkPlan(file);
  } catch {
    // Not an MP4 or parsing failed — sequential chunking
    const chunks: ChunkDescriptor[] = [];
    let idx = 0;
    for (let off = 0; off < file.size; off += CHUNK_SIZE) {
      chunks.push({
        index: idx++,
        source: 'file',
        fileOffset: off,
        fileSize: Math.min(CHUNK_SIZE, file.size - off),
      });
    }
    return { chunks, totalSize: file.size, isRelocated: false, originalFilename: file.name };
  }
}

/**
 * Get the blob for a single chunk descriptor.
 *
 * @reason Handles both file-backed and buffer-backed chunks from the chunk plan.
 */
function getChunkBlob(file: File, chunk: ChunkDescriptor): Blob {
  if (chunk.source === 'buffer' && chunk.buffer) {
    return new Blob([chunk.buffer], { type: 'application/octet-stream' });
  }
  return file.slice(chunk.fileOffset!, chunk.fileOffset! + chunk.fileSize!);
}

/**
 * Upload a single chunk with retry and exponential backoff.
 *
 * @reason Network interruptions are common for large uploads; retry avoids restarting from scratch.
 */
async function uploadChunkWithRetry(url: string, blob: Blob, signal: AbortSignal): Promise<void> {
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(url, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': 'application/octet-stream' },
        signal,
      });
      // Explicitly consume the response body so Safari releases the
      // request/response memory (including the uploaded blob).
      await response.body?.cancel();
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return;
    } catch (err: any) {
      if (signal.aborted) throw err;
      if (attempt === MAX_RETRIES - 1) throw err;
      // Exponential backoff: 1s, 2s, 4s
      await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
    }
  }
}

type UploadDialogProps = {
  open: boolean;
  disableBackdropClose?: boolean;
  selectedFile: File | null;
  onClose: () => void;
};

const UploadDialog: FC<UploadDialogProps> = ({
  open,
  disableBackdropClose,
  selectedFile,
  onClose,
}) => {
  const { data: streamKeysData, isLoading: isStreamKeysLoading } = useGetStreamKeys();
  const streamKeys = useMemo(() => streamKeysData?.items?.filter(Boolean) ?? [], [streamKeysData]);
  const [selectedStreamKeyId, setSelectedStreamKeyId] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgressValue, setUploadProgressValue] = useState<number>(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { data: projects = [], refetch: refetchProjects } = useGetProjectList();

  useEffect(() => {
    if (!selectedStreamKeyId && streamKeys.length > 0) {
      setSelectedStreamKeyId(streamKeys[0].id);
    }
  }, [streamKeys, selectedStreamKeyId]);

  const selectedKey = useMemo(
    () => streamKeys.find((k: any) => k.id === selectedStreamKeyId),
    [streamKeys, selectedStreamKeyId],
  );

  useEffect(() => {
    if (!open) {
      setUploadError('');
    }
  }, [open]);

  /**
   * Upload all chunks in parallel with a concurrency limit.
   *
   * @reason Uploading 4 chunks at a time maximises bandwidth without overwhelming the browser.
   */
  const uploadChunks = async (
    file: File,
    chunkPlan: ChunkPlan,
    presignedUrls: string[],
    signal: AbortSignal,
  ) => {
    let completedChunks = 0;
    const totalChunks = chunkPlan.chunks.length;
    let nextChunkIndex = 0;

    const worker = async () => {
      while (nextChunkIndex < totalChunks) {
        if (signal.aborted) return;
        const idx = nextChunkIndex++;
        const chunk = chunkPlan.chunks[idx];
        const blob = getChunkBlob(file, chunk);
        await uploadChunkWithRetry(presignedUrls[idx], blob, signal);
        // Release the in-memory moov buffer after its chunk is uploaded so it
        // can be garbage-collected instead of living for the entire upload.
        if (chunk.source === 'buffer') {
          chunk.buffer = undefined;
        }
        completedChunks++;
        const progressPercent = Math.round((completedChunks / totalChunks) * 100);
        setUploadProgressValue(progressPercent);
        setUploadStatus(
          `Uploading: ${progressPercent}% (${completedChunks}/${totalChunks} chunks, ${Math.round(
            (completedChunks * CHUNK_SIZE) / (1024 * 1024),
          )} MB)`,
        );
        // Yield to the event loop between chunks so Safari's GC has a chance
        // to reclaim the blob and fetch buffers from the previous iteration.
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    };

    const workers = Array.from({ length: Math.min(MAX_CONCURRENT_UPLOADS, totalChunks) }, () =>
      worker(),
    );
    await Promise.all(workers);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (disableBackdropClose && reason === 'backdropClick') return;
        if (!isUploading) onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Upload Video to Stream</DialogTitle>
      <DialogContent>
        {selectedFile && (
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              Selected file: {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)}{' '}
              MB)
            </Typography>
          </Box>
        )}

        <FormControl fullWidth margin="normal">
          <InputLabel>Select Stream Key</InputLabel>
          <Select
            value={selectedStreamKeyId}
            onChange={(event) => setSelectedStreamKeyId(event.target.value as string)}
            disabled={isUploading || isStreamKeysLoading || streamKeys.length === 0}
            label="Select Stream Key"
          >
            {streamKeys.map((key: any) => (
              <MenuItem key={key.id} value={key.id}>
                {key.name} ({key.type})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {uploadError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {uploadError}
          </Alert>
        )}

        {isUploading && (
          <Box mt={2}>
            <LinearProgress
              variant={uploadProgressValue > 0 ? 'determinate' : 'indeterminate'}
              value={uploadProgressValue}
              sx={{ mb: 1 }}
            />

            {uploadStatus && (
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 1 }}>
                {uploadStatus}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <DefaultButton
          onClick={() => {
            if (isUploading && abortControllerRef.current) {
              setUploadStatus('Cancelling upload...');
              abortControllerRef.current.abort();
              setIsUploading(false);
            } else {
              onClose();
            }
          }}
        >
          {isUploading ? 'Cancel Upload' : 'Cancel'}
        </DefaultButton>
        <PrimaryButton
          onClick={async () => {
            if (!selectedFile || !selectedKey) return;
            setIsUploading(true);
            setUploadError('');
            setUploadProgressValue(0);
            setUploadStatus('Analyzing video...');

            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            try {
              // Step 1: Parse MP4 and build chunk plan
              const chunkPlan = await getChunkPlan(selectedFile);

              if (abortController.signal.aborted) return;

              // Step 2: Create upload session (get presigned URLs)
              setUploadStatus('Preparing upload...');
              const session = await Api.post<any>('/upload/create-session', {
                streamKey: selectedKey.streamKey,
                fileName: selectedFile.name,
                totalChunks: chunkPlan.chunks.length,
                totalSize: chunkPlan.totalSize,
              });

              if (abortController.signal.aborted) return;

              // Step 3: Upload chunks in parallel
              await uploadChunks(
                selectedFile,
                chunkPlan,
                session.presignedUrls,
                abortController.signal,
              );

              if (abortController.signal.aborted) return;

              // Step 4: Upload manifest
              setUploadStatus('Finalizing upload...');
              const manifest = {
                uploadId: session.uploadId,
                totalChunks: chunkPlan.chunks.length,
                totalSize: chunkPlan.totalSize,
                originalFilename: chunkPlan.originalFilename,
                isRelocated: chunkPlan.isRelocated,
                chunkSizeBytes: CHUNK_SIZE,
              };
              await fetch(session.manifestUrl, {
                method: 'PUT',
                body: JSON.stringify(manifest),
                headers: { 'Content-Type': 'application/json' },
                signal: abortController.signal,
              });

              // Step 5: Upload complete — streaming will start automatically
              setUploadStatus('Video uploaded! Streaming will begin shortly.');
              setIsUploading(false);

              enqueueSnackbar('Video uploaded successfully. Streaming will begin shortly.', {
                variant: 'success',
              });
              setTimeout(() => {
                onClose();
              }, 2000);
            } catch (err: any) {
              if (abortController.signal.aborted) {
                setUploadError('Upload was cancelled.');
              } else {
                setUploadError(err?.message || 'Failed to upload video');
              }
              setIsUploading(false);
            } finally {
              abortControllerRef.current = null;
            }
          }}
          disabled={!selectedFile || !selectedStreamKeyId || isUploading || isStreamKeysLoading}
        >
          {isUploading ? 'Uploading...' : 'Start Upload'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

const UploadButton: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
      setIsUploadDialogOpen(true);
    } else {
      enqueueSnackbar('Please select a valid video file', { variant: 'error' });
    }
    event.target.value = '';
  };

  return (
    <>
      <PrimaryButton
        onClick={() => {
          const fileInput = document.createElement('input');
          fileInput.type = 'file';
          fileInput.accept = 'video/*';
          fileInput.onchange = (event) => {
            handleFileSelect(event as unknown as React.ChangeEvent<HTMLInputElement>);
          };
          fileInput.click();
        }}
      >
        <div
          className={styles.buttonLabel + ' ' + styles.backButtonLabel}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          Upload <UploadIcon fontSize="small" />
        </div>
      </PrimaryButton>

      <UploadDialog
        open={isUploadDialogOpen}
        disableBackdropClose
        selectedFile={selectedFile}
        onClose={() => {
          setIsUploadDialogOpen(false);
          setSelectedFile(null);
        }}
      />
    </>
  );
};

export default UploadButton;
